import { useState, type ReactNode } from 'react'
import { ChevronRight, CircleAlert, Info, TriangleAlert, X } from 'lucide-react'
import { cx } from '@/lib/clases'
import { BORDE_IZQUIERDO, FONDO_TENUE, type Tono } from './tonos'

/**
 * Banner. **Los modales para errores están prohibidos:** interrumpen, borran el
 * contexto y obligan a un clic extra para volver a ver lo que estaba mal.
 *
 * Dónde va cada error (docs/07-frontend.md):
 *   validación de campo    debajo del campo, en rojo con icono
 *   error de la operación  banner arriba del formulario  ← esto
 *   error de fondo         notificación discreta abajo a la derecha
 *   bloqueo total          banner fijo en toda la aplicación, no cerrable
 */

const ICONO: Record<Tono, typeof Info> = {
  neutral: Info,
  marca: Info,
  azul: Info,
  verde: Info,
  ambar: TriangleAlert,
  rojo: CircleAlert,
}

export interface PropsBanner {
  tono?: Tono
  /** En negrita: el hecho. "El RNC del cliente no existe en DGII." */
  titulo: ReactNode
  /** Una línea de contexto. Qué pasó y por qué. */
  children?: ReactNode
  /** El botón que lleva al lugar donde se arregla. */
  accion?: ReactNode
  /** Si se pasa, el banner se puede cerrar. Un bloqueo total no se pasa nunca. */
  onCerrar?: () => void
  /** Código o respuesta cruda, plegada, para soporte. */
  detalleTecnico?: string
  className?: string
}

export function Banner({
  tono = 'azul',
  titulo,
  children,
  accion,
  onCerrar,
  detalleTecnico,
  className,
}: PropsBanner) {
  const [abierto, setAbierto] = useState(false)
  const Icono = ICONO[tono]

  return (
    <div
      role={tono === 'rojo' ? 'alert' : 'status'}
      className={cx(
        'flex items-start gap-3 rounded-md border border-gris-300 border-l-[3px] px-3 py-2',
        FONDO_TENUE[tono],
        BORDE_IZQUIERDO[tono],
        className,
      )}
    >
      <Icono aria-hidden="true" className="mt-px size-4 shrink-0 text-gris-700" />

      <div className="min-w-0 flex-1">
        <p className="text-cuerpo font-semibold text-gris-900">{titulo}</p>
        {children ? <div className="text-cuerpo text-gris-700">{children}</div> : null}

        {detalleTecnico ? (
          <div className="mt-1">
            <button
              type="button"
              onClick={() => setAbierto((v) => !v)}
              aria-expanded={abierto}
              className="inline-flex items-center gap-1 rounded-md text-etiqueta text-gris-600 hover:text-gris-900"
            >
              <ChevronRight
                aria-hidden="true"
                className={cx(
                  'size-3 transition-transform duration-[var(--duracion-corta)]',
                  abierto && 'rotate-90',
                )}
              />
              Detalle técnico
            </button>
            {abierto ? (
              <pre className="mono mt-1 max-h-40 overflow-auto rounded-md border border-gris-300 bg-white p-2 text-gris-700">
                {detalleTecnico}
              </pre>
            ) : null}
          </div>
        ) : null}

        {accion ? <div className="mt-2 flex gap-2">{accion}</div> : null}
      </div>

      {onCerrar ? (
        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar aviso"
          className="shrink-0 rounded-md p-1 text-gris-600 hover:bg-white hover:text-gris-900"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      ) : null}
    </div>
  )
}
